import React, { useEffect, useRef, useState } from "react";
import { Drawer, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import MultiValueWidget from "../../../../../components/charts/MultiValueWidget";
import { View } from "../../../../../components/layout/View";
import { PageType } from "../../../../../config/page.types";
import { useBreadcrumbs } from "../../../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import { TripDetailsDTO } from "../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../hooks/useResource";
import TripEmissionDetails from "./components/TripEmissionDetails";
import { TripEntities } from "./components/TripEntities";
import TripMap from "./components/TripMap";
import { displayValues } from "./data";

const TransportGreenrouterDetail = () => {
	const { t, i18n } = useTranslation(["feature.results", "common"]);
	const { visitPage } = useBreadcrumbs();

	const { appliedFilters, registry } = useFiltersRegistry();

	const params = useParams();
	const id = params.id || "1"; // NOTE: redirect in case of id not given
	const [showEntitiesPanel, setShowEntitiesPanel] = useState<boolean>(false);
	const [visibleEntities, setVisibleEntities] = useState<string[]>([]);

	const firstArcEntitiesRef = useRef<HTMLDivElement | null>(null);
	const [scrollToEntities, setScrollToEntities] = useState(false);

	useEffect(() => {
		if (!scrollToEntities) return;

		requestAnimationFrame(() => {
			firstArcEntitiesRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});

			setScrollToEntities(false);
		});
	}, [scrollToEntities, visibleEntities]);

	const hideEntities = (list: string[]) => {
		const newSet = new Set(visibleEntities);
		list.forEach((entity) => newSet.delete(entity));
		setVisibleEntities([...newSet]);
	};

	const showEntities = (list: string[]) => {
		setShowEntitiesPanel(false);

		setVisibleEntities((prev) => {
			const newSet = new Set(prev);
			list.forEach((entity) => newSet.add(entity));
			return [...newSet];
		});

		setScrollToEntities(true);
	};

	useEffect(() => {
		if (id) {
			visitPage(PageType.ResultsTransportGreenRouterDetail, false, id, id);
		}
	}, [id, visitPage]);

	const { data, isLoading, reload } = useReadResource<undefined, TripDetailsDTO>({
		route: ApiRoutes.ResultsTripDetails,
		path: { id: id },
	});

	const { transportData, transportTotalEmissions, appliedFiltersToDetail } = displayValues(
		data,
		t,
		i18n.language,
		appliedFilters.payload,
		registry
	);

	const showEntitiesAction =
		data?.entitiesCount && data.entitiesCount > 0
			? {
					title: t("feature.results:tripDetails/show entities", {
						count: data.entitiesCount,
					}),
					onClick: () => setShowEntitiesPanel(true),
				}
			: undefined;

	const headerContext = {
		refresh: reload,
		loading: isLoading,
	};

	return (
		<View pageType={PageType.ResultsTransportGreenRouterDetail} headerContext={headerContext}>
			<Grid container spacing={2}>
				<Grid size={12} minHeight={128}>
					<MultiValueWidget
						isLoading={isLoading}
						values={transportTotalEmissions}
						valueVariant="h5"
						title={t("tripDetails/transport total emissions")}
						elementsPerRow={6}
					/>
				</Grid>
				<Grid size={12} minHeight={150}>
					<MultiValueWidget
						isLoading={isLoading}
						values={transportData}
						valueVariant="body1"
						title={t("tripDetails/transport data")}
						elementsPerRow={5}
					/>
				</Grid>
				{appliedFiltersToDetail.length > 0 && (
					<Grid size={12} minHeight={150}>
						<MultiValueWidget
							isLoading={isLoading}
							values={appliedFiltersToDetail}
							valueVariant="body1"
							title={t("applied filters", { ns: "common" })}
							elementsPerRow={5}
							areFilters={true}
						/>
					</Grid>
				)}
				<Grid size={12} minHeight={500}>
					<TripMap arcs={data?.arcs} isLoading={isLoading} />
				</Grid>
				<Grid size={12} minHeight={500}>
					<TripEmissionDetails
						arcs={data?.arcs}
						visibleEntities={visibleEntities}
						isLoading={isLoading}
						entitiesScrollRef={firstArcEntitiesRef}
						action={showEntitiesAction}
					/>
				</Grid>
			</Grid>

			<Drawer
				anchor="right"
				open={showEntitiesPanel}
				onClose={() => setShowEntitiesPanel(false)}
				slotProps={{
					paper: {
						sx: {
							minWidth: "50%",
							maxWidth: "50%",
						},
					},
				}}
			>
				<TripEntities
					transportId={id}
					descriptionTrip={data?.description ?? id}
					visibleEntities={visibleEntities}
					showEntities={showEntities}
					hideEntities={hideEntities}
				/>
			</Drawer>
		</View>
	);
};

export default TransportGreenrouterDetail;
